import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import './PaymentStripe.css';

function PaymentStripeReturn() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const stripeCheckoutId = searchParams.get('stripe_checkout_id');
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('verifying');
  const [completedOrderId, setCompletedOrderId] = useState(null);

  const invalidateCart = () => {
    queryClient.invalidateQueries({ queryKey: ['cart'] });
  };

  const completeStripeMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token');
      const sessionIdHeader = localStorage.getItem('sessionId');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (sessionIdHeader) headers['x-session-id'] = sessionIdHeader;
      const res = await apiClient.post('payment/complete-stripe', {
        stripe_checkout_id: stripeCheckoutId,
        session_id: sessionId,
      }, { headers });
      return res.data;
    },
    onSuccess: (data) => {
      const id = data?.order?.id;
      if (id) {
        invalidateCart();
        setCompletedOrderId(id);
        setStatus('success');
        setTimeout(() => navigate(`/orders/${id}`), 3000);
      } else {
        setStatus('error');
      }
    },
    onError: () => setStatus('error'),
  });

  const verifyPaymentMutation = useMutation({
    mutationFn: async () => {
      if (!orderId) throw new Error('Order ID is required');
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const requestData = { order_id: parseInt(orderId), payment_method: 'stripe' };
      if (sessionId) requestData.session_id = sessionId;
      const res = await apiClient.post('/payment/verify', requestData, { headers });
      return res.data;
    },
    onSuccess: (data) => {
      const successStatuses = ['succeeded', 'paid', 'complete', 'completed'];
      const statusLower = data.status?.toLowerCase();
      const isSuccess = successStatuses.includes(statusLower) || data.is_paid === true;
      if (isSuccess) {
        invalidateCart();
        setStatus('success');
        setTimeout(() => navigate(`/orders/${orderId}`), 3000);
      } else {
        setStatus('error');
      }
    },
    onError: () => setStatus('error'),
  });

  useEffect(() => {
    if (stripeCheckoutId && sessionId) {
      completeStripeMutation.mutate();
      return;
    }
    if (orderId && sessionId) {
      verifyPaymentMutation.mutate();
      return;
    }
    if (orderId && !sessionId) {
      verifyPaymentMutation.mutate();
      return;
    }
    if (!stripeCheckoutId && !orderId) {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeCheckoutId, orderId, sessionId]);

  const viewOrderId = completedOrderId || orderId;

  if (status === 'verifying') {
    return (
      <div className="payment-confirm-page">
        <div className="payment-confirm-container">
          <div className="payment-confirm-header">
            <h1>{t('paymentVerifying')}</h1>
            <p className="payment-confirm-subtitle">{t('paymentVerifyingHint')}</p>
          </div>
          <div className="payment-confirm-card payment-confirm-verifying">
            <div className="payment-confirm-loading" aria-hidden="true" />
            <p className="payment-confirm-loading-text">{t('paymentVerifyingHint')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="payment-confirm-page">
        <div className="payment-confirm-container">
          <div className="payment-confirm-header">
            <h1>{t('paymentSuccessTitle')}</h1>
            <p className="payment-confirm-subtitle">{t('paymentSuccessText')}</p>
          </div>
          <div className="payment-confirm-card payment-confirm-success">
            <div className="payment-confirm-icon payment-confirm-icon-success" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <p className="payment-confirm-message">{t('paymentSuccessText')}</p>
            {viewOrderId && (
              <p className="payment-confirm-redirect">
                {t('redirectingToOrder') || 'Peradresuojame į užsakymą...'}
              </p>
            )}
            <div className="payment-confirm-actions">
              {viewOrderId && (
                <button type="button" onClick={() => navigate(`/orders/${viewOrderId}`)} className="btn btn-primary">
                  {t('viewOrder')}
                </button>
              )}
              <Link to="/orders" className="btn btn-secondary">
                {t('backToOrders') || 'Mano užsakymai'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-confirm-page">
      <div className="payment-confirm-container">
        <div className="payment-confirm-header">
          <h1>{t('paymentConfirmFailed')}</h1>
          <p className="payment-confirm-subtitle">{t('paymentConfirmErrorText')}</p>
        </div>
        <div className="payment-confirm-card payment-confirm-error">
          <div className="payment-confirm-icon payment-confirm-icon-error" aria-hidden="true">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M15 9l-6 6M9 9l6 6" />
            </svg>
          </div>
          <p className="payment-confirm-message">{t('paymentConfirmErrorText')}</p>
          <p className="payment-confirm-hint">{t('checkConsoleHint')}</p>
          <div className="payment-confirm-actions">
            {viewOrderId && (
              <button type="button" onClick={() => navigate(`/orders/${viewOrderId}`)} className="btn btn-primary">
                {t('viewOrder')}
              </button>
            )}
            <button type="button" onClick={() => navigate('/cart')} className="btn btn-secondary">
              {t('backToCart') || 'Į krepšelį'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentStripeReturn;
